"use client";

import { IconArrowNarrowRight } from "@tabler/icons-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslations } from "next-intl";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import CustomChoice from "@/assets/icons/diet-types/custom-choice.svg";
import Reductive from "@/assets/icons/diet-types/reductive.svg";
import Standard from "@/assets/icons/diet-types/standard.svg";
import Vegetarian from "@/assets/icons/diet-types/vegetarian.svg";
import { Img } from "@/components/image";
import { buttonVariants } from "@/components/ui/button";
import type { HowItWorksType } from "@/features/static-builder/types/how-it-works.type";
import { Link } from "@/i18n/navigation";
import { strapiUrl } from "@/lib/strapi";
import { cn } from "@/lib/utils";

export const HowItWorks = ({ data }: { data: HowItWorksType }) => {
  const t = useTranslations();
  const shouldReduceMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [indicator, setIndicator] = useState({ top: 0, height: 0 });
  const listRef = useRef<HTMLOListElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const imageUrl = data.image?.url ? strapiUrl(data.image.url) : null;

  const iconClassName = "h-6 w-6";

  const badgeClassName =
    "absolute inline-flex items-center gap-2 rounded-full bg-brand-white p-3 font-medium text-base leading-6 shadow-[0px_2px_3px_rgba(0,0,0,0.08)] border";

  const badges = [
    {
      id: "menu",
      label: t("dietTags.CUSTOM_CHOICE"),
      icon: <CustomChoice className={iconClassName} />,
      className: "-left-6 top-16 md:left-8 md:top-16",
    },
    {
      id: "reduction",
      label: t("dietTags.REDUCTIVE"),
      icon: <Reductive className={iconClassName} />,
      className: "right-12 -top-6 md:right-20 md:top-6",
    },
    {
      id: "vegetarian",
      label: t("dietTags.VEGETARIAN"),
      icon: <Vegetarian className={iconClassName} />,
      className: "-right-12 top-1/2 md:-right-10",
    },
    {
      id: "standard",
      label: t("dietTags.STANDARD"),
      icon: <Standard className={iconClassName} />,
      className: "bottom-6 left-6 md:left-46 md:bottom-12",
    },
  ] as const;

  const updateIndicator = useCallback(() => {
    const listElement = listRef.current;
    const activeElement = itemRefs.current[activeIndex];

    if (!listElement) {
      return;
    }

    if (!activeElement) {
      return;
    }

    const listRect = listElement.getBoundingClientRect();
    const activeRect = activeElement.getBoundingClientRect();

    setIndicator({
      top: activeRect.top - listRect.top,
      height: activeRect.height,
    });
  }, [activeIndex]);

  useLayoutEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  useEffect(() => {
    if (shouldReduceMotion) {
      updateIndicator();
      return;
    }

    let frameId = 0;
    let startTime = 0;
    const animationDuration = 400;

    const syncIndicatorPosition = (timestamp: number) => {
      if (startTime === 0) {
        startTime = timestamp;
      }

      updateIndicator();

      if (timestamp - startTime < animationDuration) {
        frameId = window.requestAnimationFrame(syncIndicatorPosition);
      }
    };

    frameId = window.requestAnimationFrame(syncIndicatorPosition);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [shouldReduceMotion, updateIndicator]);

  useEffect(() => {
    const handleResize = () => {
      updateIndicator();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [updateIndicator]);

  return (
    <section className="mt-8 mb-18 overflow-hidden bg-brand-grey4 pt-10 md:my-20 md:py-10">
      <div className="container">
        <div className="mb-8 flex flex-col gap-4 md:mb-10 md:flex-row md:items-center md:justify-between">
          <h2
            className="h2"
            dangerouslySetInnerHTML={{ __html: data.heading }}
          />
          {/* Desktop */}
          <Link
            href="/diets"
            className={cn(
              buttonVariants({ variant: "secondary", size: "lg" }),
              "h-12 px-5 max-md:hidden md:w-auto",
            )}
            title={t("header.orderDiet")}
          >
            {t("header.orderDiet")}
            <IconArrowNarrowRight className="ml-2 [&>path]:stroke-current" />
          </Link>
        </div>

        <div className="flex flex-col justify-between gap-12 md:gap-8 lg:flex-row lg:items-center">
          <div className="relative w-full max-w-120 pl-6 md:pl-8">
            <div className="absolute top-0 left-0 h-full w-[8px] rounded-full bg-brand-grey2/40" />
            <motion.div
              className="absolute left-0 w-[8px] rounded-full bg-brand-green2-300"
              animate={{
                top: indicator.top,
                height: indicator.height,
              }}
              transition={
                shouldReduceMotion
                  ? { duration: 0 }
                  : {
                      type: "spring",
                      stiffness: 260,
                      damping: 30,
                      mass: 0.8,
                    }
              }
            />

            <ol ref={listRef}>
              {data.steps.map((step, index) => (
                <li key={step.id} className="relative">
                  <button
                    ref={(element) => {
                      itemRefs.current[index] = element;
                    }}
                    type="button"
                    onClick={() => {
                      setActiveIndex(index);
                    }}
                    className="block w-full py-4 text-left"
                    aria-expanded={activeIndex === index}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-brand-yellow-dark text-sm leading-4.5">
                        0{String(index + 1)}
                      </span>
                      <h3
                        className={cn(
                          "text-xl leading-7 transition-colors duration-300",
                          {
                            "font-bold text-brand-green2-300":
                              activeIndex === index,
                            "font-medium text-black/60": activeIndex !== index,
                          },
                        )}
                      >
                        {step.title}
                      </h3>
                    </div>

                    <AnimatePresence initial={false} mode="wait">
                      {activeIndex === index && step.description && (
                        <motion.div
                          key={step.id}
                          initial={
                            shouldReduceMotion
                              ? { opacity: 1, height: "auto" }
                              : { opacity: 0, height: 0, y: -6 }
                          }
                          animate={
                            shouldReduceMotion
                              ? { opacity: 1, height: "auto" }
                              : { opacity: 1, height: "auto", y: 0 }
                          }
                          exit={
                            shouldReduceMotion
                              ? { opacity: 0, height: 0 }
                              : { opacity: 0, height: 0, y: -6 }
                          }
                          transition={{ duration: 0.28, ease: "easeOut" }}
                          className="overflow-hidden"
                        >
                          <p className="max-w-90 pt-3 pb-8 text-base text-brand-grey5 leading-6">
                            {step.description}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </button>
                </li>
              ))}
            </ol>
          </div>
          <Link
            href="/diets"
            className={cn(
              buttonVariants({ variant: "secondary", size: "lg" }),
              "w-full px-5 md:hidden",
            )}
            title={t("header.orderDiet")}
          >
            {t("header.orderDiet")}
            <IconArrowNarrowRight className="ml-2 [&>path]:stroke-current" />
          </Link>
          {/* Desktop */}
          {imageUrl && (
            <div className="relative shrink-0 max-lg:hidden">
              <Img
                src={imageUrl}
                alt={data.heading}
                width={700}
                height={1100}
                className="w-[640px] rounded-2xl object-cover"
              />

              {badges.map((badge) => (
                <div
                  key={badge.id}
                  className={cn(badgeClassName, badge.className)}
                >
                  {badge.icon}
                  {badge.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Mobile */}
      {imageUrl && (
        <div className="relative mt-12 lg:hidden">
          <Img
            src={imageUrl}
            alt={data.heading}
            width={700}
            height={1100}
            className="h-[460px] w-full object-cover"
          />

          {badges.map((badge) => (
            <div key={badge.id} className={cn(badgeClassName, badge.className)}>
              {badge.icon}
              {badge.label}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
